import { Avatar, Button, Modal, ModalBody, ModalContent, Spinner } from "@heroui/react";
import * as dn from "dnum";
import { BadgeCheckIcon, ShieldCheckIcon } from "lucide-react";
import { usePrivateBalance } from "../../queries/useTokenBalances";
import { formatTimestamp } from "../../utils";
import { TxHashLink } from "../TxHashLink";
import type { ShieldingTxSuccess } from "./shieldingConfig";

export function ShieldingSuccessModal({
  isOpen,
  onClose,
  shieldingResult,
}: {
  isOpen: boolean;
  onClose: () => void;
  shieldingResult: ShieldingTxSuccess | null;
}) {
  const { privateBalance, isLoading: balanceLoading } = usePrivateBalance(
    shieldingResult?.tokenAddress.toString() || "",
    isOpen && shieldingResult?.shieldingMode === "shield",
  );

  if (!shieldingResult) {
    return null;
  }

  const formattedPrivateBalance = balanceLoading
    ? "Loading..."
    : privateBalance !== undefined
      ? `${dn.format([privateBalance, shieldingResult.tokenDecimals], { digits: 4, decimalsRounding: "ROUND_DOWN" })} ${shieldingResult.tokenSymbol}`
      : `${shieldingResult.displayAmount} ${shieldingResult.tokenSymbol}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      classNames={{
        base: "turnstile-modal-border rounded-3xl",
        closeButton: "text-white text-xl top-4 right-5",
        header: "pt-5 pb-3",
        body: "items-center p-6 pt-0 gap-4",
        backdrop: "turnstile-modal-backdrop",
      }}
    >
      <ModalContent>
        <ModalBody className="flex items-center gap-4 p-8">
          <BadgeCheckIcon className="size-24 stroke-1 text-green-500/90 fill-green-500/10 " />

          <p className="font-semibold text-large">
            {shieldingResult.shieldingMode === "shield" ? "Shielding" : "Unshielding"} completed!
          </p>

          <div className="flex items-center justify-between gap-4 py-4 px-8 w-full rounded-large bg-[#3F6784]">
            <div className="flex items-center gap-2">
              <Avatar color="primary" name="T" classNames={{ base: "!size-8" }} />
              <span className="font-semibold">
                {shieldingResult.displayAmount} {shieldingResult.tokenSymbol}
              </span>
            </div>

            <div className="flex items-center gap-1 text-[#14DBC0]">
              <ShieldCheckIcon />
              <span className="font-semibold">
                {shieldingResult.shieldingMode === "shield" ? "Shielded" : "Unshielded"}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2 w-full">
            <div className="flex justify-between w-full">
              <div className="text-[#ADC0D0]">Private balance</div>
              <div className="font-semibold">
                {balanceLoading ? <Spinner size="sm" /> : formattedPrivateBalance}
              </div>
            </div>

            <div className="flex justify-between w-full">
              <div className="text-[#ADC0D0]">Date & time</div>
              <div className="font-semibold">{formatTimestamp(shieldingResult.timestamp)}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="text-[#ADC0D0]">Transaction Hash</div>
              <div className="font-semibold">
                <TxHashLink txHash={shieldingResult.txHash} source="l2" />
              </div>
            </div>
          </div>

          <Button fullWidth color="primary" size="lg" radius="full" onPress={onClose}>
            Close
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
